import { css, cx } from '@emotion/css';
import type { HTMLAttributes } from 'react';
import { FiCalendar, FiMapPin, FiClock } from 'react-icons/fi';

export interface ProfileEventCardData {
  id: string;
  title: string;
  imageUrl?: string | null;
  location?: string;
  date?: string;
  badge?: {
    text: string;
    variant?: 'waiting' | 'available' | 'default';
  };
}

export interface ProfileEventCardProps extends HTMLAttributes<HTMLDivElement> {
  event: ProfileEventCardData;
  onEventClick?: (eventId: string) => void;
}

// Styles
const card = css({
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--primary-color)',
  borderRadius: 'var(--spacing-xs)',
  overflow: 'hidden',
  boxShadow: 'var(--shadow-100)',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  border: '1px solid var(--g-200)',
  height: '100%',
  '&:hover': {
    boxShadow: 'var(--shadow-200)',
    borderColor: 'var(--g-300)',
  },
});

const imageWrapper = css({
  width: '100%',
  height: '160px',
  overflow: 'hidden',
  position: 'relative',
  backgroundColor: 'var(--pc-200)',
});

const image = css({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 0.3s ease',
  display: 'block',
  '.profile-event-card:hover &': {
    transform: 'scale(1.05)',
  },
});

const imagePlaceholder = css({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'var(--fs-700)',
  fontWeight: 700,
  color: 'var(--pc-600)',
  backgroundColor: 'var(--pc-200)',
});

const badge = css({
  position: 'absolute',
  bottom: 'var(--spacing-xs)',
  right: 'var(--spacing-xs)',
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-3xs)',
  padding: 'var(--spacing-3xs) var(--spacing-xs)',
  borderRadius: 'var(--spacing-3xs)',
  fontSize: 'var(--fs-300)',
  fontWeight: 500,
  zIndex: 5,
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-250)',
    padding: 'var(--spacing-4xs) var(--spacing-2xs)',
  },
});

const badgeWaiting = css({
  backgroundColor: 'rgba(var(--pcrgba-600), 0.85)',
  color: 'white',
});

const badgeAvailable = css({
  backgroundColor: 'rgba(72, 187, 120, 0.85)',
  color: 'white',
});

const badgeDefault = css({
  backgroundColor: 'rgba(var(--g-rgb-600), 0.85)',
  color: 'white',
});

const content = css({
  padding: 'var(--spacing-md)',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  '@media (max-width: 480px)': {
    padding: 'var(--spacing-sm)',
  },
});

const title = css({
  fontSize: 'var(--fs-400)',
  fontWeight: 600,
  color: 'var(--g-800)',
  marginBottom: 'var(--spacing-sm)',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minHeight: '2.8em',
  '@media (max-width: 480px)': {
    fontSize: 'var(--fs-350)',
  },
});

const meta = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--spacing-xs)',
});

const metaItem = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--spacing-xs)',
  color: 'var(--g-600)',
  fontSize: 'var(--fs-300)',
  '& svg': {
    color: 'var(--pc-600)',
    flexShrink: 0,
  },
  '& span': {
    margin: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

export function ProfileEventCard({
  event,
  onEventClick,
  className,
  style,
  ...props
}: ProfileEventCardProps) {
  const handleClick = () => {
    if (onEventClick) {
      onEventClick(event.id);
    }
  };

  const getBadgeClass = () => {
    switch (event.badge?.variant) {
      case 'waiting':
        return badgeWaiting;
      case 'available':
        return badgeAvailable;
      default:
        return badgeDefault;
    }
  };

  return (
    <div
      className={cx(card, 'profile-event-card', className)}
      onClick={handleClick}
      style={style}
      {...props}
    >
      <div className={imageWrapper}>
        {event.imageUrl ? (
          <img
            src={event.imageUrl}
            alt={event.title}
            className={image}
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const placeholder = target.parentElement?.querySelector(
                `.${imagePlaceholder}`
              );
              if (placeholder) {
                (placeholder as HTMLElement).style.display = 'flex';
              }
            }}
          />
        ) : null}
        <div
          className={imagePlaceholder}
          style={{ display: event.imageUrl ? 'none' : 'flex' }}
        >
          {event.title?.charAt(0).toUpperCase() || 'E'}
        </div>

        {event.badge && (
          <div className={cx(badge, getBadgeClass())}>
            <FiClock size={14} />
            <span>{event.badge.text}</span>
          </div>
        )}
      </div>

      <div className={content}>
        <h3 className={title}>{event.title}</h3>

        <div className={meta}>
          {event.location && (
            <div className={metaItem}>
              <FiMapPin size={16} />
              <span>{event.location}</span>
            </div>
          )}

          {event.date && (
            <div className={metaItem}>
              <FiCalendar size={16} />
              <span>{event.date}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileEventCard;
